import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { ENDPOINT_PERMISSIONS } from './permissions.config';
import { Router } from '@angular/router';
import { Roles } from './roles.enum';

export interface RouteAccess {
  path: string;
  roles: string[];
  priority: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private keycloak = inject(Keycloak);
  private router = inject(Router);

  private readonly availableRoutes: RouteAccess[] = [
    {
      path: 'observer',
      roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_OBSERVER, Roles.WEBRQA_VIEWER],
      priority: 1
    },
    { path: 'orders', roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_VIEWER], priority: 2 },
    { path: 'messages', roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_VIEWER], priority: 3 },
    {
      path: 'user-config',
      roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_VIEWER, Roles.WEBRQA_OBSERVER],
      priority: 4
    },
    {
      path: 'help',
      roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_VIEWER, Roles.WEBRQA_OBSERVER],
      priority: 5
    },
    {
      path: 'privacy-policy',
      roles: [Roles.WEBRQA_USER, Roles.WEBRQA_DRAW, Roles.WEBRQA_VIEWER, Roles.WEBRQA_OBSERVER],
      priority: 6
    }
  ];

  hasRole(role: string): boolean {
    return this.keycloak.hasResourceRole(role);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.keycloak.hasResourceRole(role));
  }

  hasAllRoles(roles: string[]): boolean {
    return roles.every((role) => this.hasRole(role));
  }

  canAccessEndpoint(endpoint: string): boolean {
    const requiredRoles = this.getRequiredRoles(endpoint);
    if (!requiredRoles) {
      return true;
    }
    return this.hasAnyRole(requiredRoles);
  }

  canAccessRoute(routePath: string): boolean {
    const route = this.availableRoutes.find((r) => r.path === routePath);
    if (!route) {
      return false;
    }

    return route.roles.some((role) => this.keycloak.hasResourceRole(role));
  }

  getFirstAccessibleRoute(): string | null {
    const sortedRoutes = [...this.availableRoutes].sort((a, b) => a.priority - b.priority);

    for (const route of sortedRoutes) {
      if (this.canAccessRoute(route.path)) {
        return route.path;
      }
    }

    return null;
  }

  redirectToFirstAccessibleRoute(): boolean {
    const firstRoute = this.getFirstAccessibleRoute();
    if (firstRoute) {
      this.router.navigate([firstRoute]);
      return true;
    }
    return false;
  }

  getAccessibleRoutes(): RouteAccess[] {
    return this.availableRoutes.filter((route) => this.canAccessRoute(route.path));
  }

  canAccessMainRoute(): boolean {
    return this.keycloak.hasResourceRole(Roles.WEBRQA_USER) || this.keycloak.hasResourceRole(Roles.WEBRQA_DRAW);
  }

  private getRequiredRoles(endpoint: string): string[] | null {
    for (const [pattern, permission] of Object.entries(ENDPOINT_PERMISSIONS)) {
      if (endpoint.includes(pattern)) {
        return permission.roles;
      }
    }
    return null;
  }
}
